import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CreateUserDTO, UpdateUserDTO, UserDTO } from '../interface/users.interface';
import { ApiService } from './app-api.service';
import { Subject } from 'rxjs';
import {
  AddKeywordDTO,
  UpdateKeywordDTO,
  AddTaxRateDTO,
  UpdateTaxRateDTO,
  EntityDTO,
} from '../interface/maintenance.interface';
import { AppCommonService } from './app-common.service';
import { ToastService } from './app-toast.service';
import { SessionStorageService } from './app-session.service';
import { AmendmentDTO } from '../interface/amended-return.interface';
import { AllIssueDTO } from '../interface/all-issues.interface';
import { AppIssuesService } from './issues.service';

@Injectable({
  providedIn: 'root',
})
export class GenericTableService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly apiService = inject(ApiService);
  private readonly commonService = inject(AppCommonService);
  private readonly toastService = inject(ToastService);
  private readonly sessionService = inject(SessionStorageService);
  private readonly issuesService = inject(AppIssuesService);

  loggedUser = this.sessionService.getItem('currentUser');
  currentDate = this.commonService.createISOTimeZone(new Date().toString());

  private refreshTableRecords = new Subject<string>();
  reloadTable$ = this.refreshTableRecords.asObservable();

  /** GENERIC: Table CRUD Status Update */
  triggerRefresh(state: string): void {
    this.refreshTableRecords.next(state);
  }

  /** =========
   * USERS_TAB: GET, ADD, UPDATE, DELETE
   */
  loadUsers() {
    return this.apiService.getUsers();
  }

  createUser(payload: CreateUserDTO): void {
    this.apiService.createUser(payload).subscribe({
      next: () => {
        this.toastService.success('User created successfully.');
        this.triggerRefresh('User');
      },
      error: (error) => {
        if (error.status === 409) {
          this.toastService.error('Duplicate Record: User already exists.');
        } else {
          console.error('Create User API Error', error);
        }
      },
    });
  }

  updateUser(payload: UpdateUserDTO): void {
    this.apiService.updateUser(payload).subscribe({
      next: () => {
        this.toastService.success('User updated successfully.');
        this.triggerRefresh('User');
      },
      error: (error) => {
        if (error.status === 409) {
          this.toastService.error('Duplicate Record: User already exists.');
        } else {
          console.error('Update User API Error', error);
        }
      },
    });
  }

  deleteUser(id: number): void {
    this.apiService
      .deleteUser(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.success('User deleted successfully.');
          this.triggerRefresh('User');
        },
        error: (error) => {
          console.error('Delete User API Error', error);
        },
      });
  }

  /** ADD: USER API PAYLOAD */
  createUserAddPayload(field: CreateUserDTO) {
    const payload: CreateUserDTO = {
      access: field.access,
      department: field.department,
      firstName: field.firstName,
      lastName: field.lastName,
      emailId: field.emailId,
      createdBy: this.loggedUser?.emailId,
      createdDate: this.currentDate,
      modifiedBy: this.loggedUser?.emailId,
      modifiedDate: this.currentDate,
    };
    return payload;
  }

  /** UPDATE: USER API PAYLOAD */
  createUserEditPayload(field: UserDTO, existingData: UpdateUserDTO) {
    const payload: UpdateUserDTO = {
      id: field.id,
      access: field.access,
      department: field.department,
      firstName: field.firstName,
      lastName: field.lastName,
      emailId: field.emailId,
      createdBy: this.loggedUser?.emailId,
      createdDate: existingData.createdDate,
      modifiedBy: this.loggedUser?.emailId,
      modifiedDate: this.currentDate,
    };
    return payload;
  }

  /** MAINTANANCE_TAB */

  /** =========
   * ENTITIES: GET, ADD, UPDATE, DELETE
   */
  loadEntities() {
    return this.apiService.getEntities();
  }

  addEntity(payload: EntityDTO): void {
    this.apiService.addEntity(payload).subscribe({
      next: () => {
        this.toastService.success('Entity created successfully.');
        this.triggerRefresh('Entity');
      },
      error: (error) => {
        if (error.status === 409) {
          this.toastService.error('Duplicate Record: Entity Code already exists.');
        } else {
          console.error('Add Entity API Error', error);
        }
      },
    });
  }

  updateEntity(payload: EntityDTO): void {
    this.apiService.updateEntity(payload).subscribe({
      next: () => {
        this.toastService.success('Entity updated successfully.');
        this.triggerRefresh('Entity');
      },
      error: (error) => {
        if (error.status === 409) {
          this.toastService.error('Duplicate Record: Entity already exists.');
        } else {
          console.error('Update Entity API Error', error);
        }
      },
    });
  }

  deleteEntity(entityCode: string): void {
    this.apiService
      .deleteEntity(entityCode)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.success('Entity deleted successfully.');
          this.triggerRefresh('Entity');
        },
        error: (error) => {
          console.error('Delete Entity API Error', error);
        },
      });
  }

  /** ADD / UPDATE: ENTITY API PAYLOAD (Entity Code is passed through even though it is read-only on edit) */
  createEntityPayload(field: EntityDTO) {
    const payload: EntityDTO = {
      entityCode: String(field.entityCode ?? '')
        .trim()
        .toUpperCase(),
      entity: String(field.entity ?? '').trim(),
    };
    return payload;
  }

  /** =========
   * AUDIT_TRAIL: GET
   */
  loadAuditTrail() {
    return this.apiService.getAuditTrail();
  }

  /** =========
   * KEYWORD: GET, ADD, UPDATE, DELETE
   */
  loadKeywords() {
    return this.apiService.getKeywords();
  }

  addKeyword(payload: AddKeywordDTO): void {
    this.apiService.addKeyword(payload).subscribe({
      next: () => {
        this.toastService.success('Keyword created successfully.');
        this.triggerRefresh('Keyword');
      },
      error: (error) => {
        if (error.status === 409) {
          this.toastService.error('Duplicate Record: Keyword already exists.');
        } else {
          console.error('Add Keyword API Error', error);
        }
      },
    });
  }

  updateKeyword(payload: UpdateKeywordDTO): void {
    this.apiService.updateKeyword(payload).subscribe({
      next: () => {
        this.toastService.success('Keyword updated successfully.');
        this.triggerRefresh('Keyword');
      },
      error: (error) => {
        if (error.status === 409) {
          this.toastService.error('Duplicate Record: Keyword already exists.');
        } else {
          console.error('Update Keyword API Error', error);
        }
      },
    });
  }

  deleteKeyword(id: number): void {
    this.apiService
      .deleteKeyword(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.success('Keyword deleted successfully.');
          this.triggerRefresh('Keyword');
        },
        error: (error) => {
          console.error('Delete User API Error', error);
        },
      });
  }

  /** ADD: KEYWORD API PAYLOAD */
  createKeywordAddPayload(field: AddKeywordDTO) {
    const payload: AddKeywordDTO = {
      keyWordCat: field.keyWordCat,
      keyWord: field.keyWord,
      createdBy: this.loggedUser?.emailId,
      createdDate: this.currentDate,
      modifiedBy: this.loggedUser?.emailId,
      modifiedDate: this.currentDate,
    };
    return payload;
  }

  /** UPDATE: KEYWORD API PAYLOAD */
  createKeywordEditPayload(field: UpdateKeywordDTO) {
    const payload: UpdateKeywordDTO = {
      id: field.id,
      keyWordCat: field.keyWordCat,
      keyWord: field.keyWord,
      createdBy: this.loggedUser?.emailId,
      createdDate: this.currentDate,
      modifiedBy: this.loggedUser?.emailId,
      modifiedDate: this.currentDate,
    };
    return payload;
  }

  /** =========
   * TAX_RATE: GET, ADD, UPDATE, DELETE
   */
  loadTaxRates() {
    return this.apiService.getTaxRates();
  }

  addTaxRate(payload: AddTaxRateDTO): void {
    this.apiService.addTaxRate(payload).subscribe({
      next: () => {
        this.toastService.success('Tax-Rate created successfully.');
        this.triggerRefresh('Tax-Rate');
      },
      error: (error) => {
        if (error.status === 409) {
          this.toastService.error('Duplicate Record: Tax Rate year already exists.');
        } else {
          console.error('Add Tax Rate API Error', error);
        }
      },
    });
  }

  updateTaxRate(payload: UpdateTaxRateDTO): void {
    this.apiService.updateTaxRate(payload).subscribe({
      next: () => {
        this.toastService.success('Tax-Rate updated successfully.');
        this.triggerRefresh('Tax-Rate');
      },
      error: (error) => {
        if (error.status === 409) {
          this.toastService.error('Duplicate Record: Tax Rate year already exists.');
        } else {
          console.error('Update Tax Rate API Error', error);
        }
      },
    });
  }

  deleteTaxRate(id: number): void {
    this.apiService
      .deleteTaxRate(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.success('Tax-Rate deleted successfully.');
          this.triggerRefresh('Tax-Rate');
        },
        error: (error) => {
          console.error('Delete User API Error', error);
        },
      });
  }

  /** ADD: KEYWORD API PAYLOAD */
  createTaxRateAddPayload(field: AddTaxRateDTO) {
    const payload: AddTaxRateDTO = {
      year: Number(field.year),
      taxRate: Number(field.taxRate),
      createdBy: this.loggedUser?.emailId,
      createdDate: this.currentDate,
      modifiedBy: this.loggedUser?.emailId,
      modifiedDate: this.currentDate,
    };
    return payload;
  }

  /** UPDATE: KEYWORD API PAYLOAD */
  createTaxRateEditPayload(field: UpdateTaxRateDTO) {
    const payload: UpdateTaxRateDTO = {
      id: field.id,
      year: Number(field.year),
      taxRate: Number(field.taxRate),
      createdBy: this.loggedUser?.emailId,
      createdDate: this.currentDate,
      modifiedBy: this.loggedUser?.emailId,
      modifiedDate: this.currentDate,
    };
    return payload;
  }

  /** =========
   * AMENDED_RETURN_TAB: GET, ADD, UPDATE, DELETE
   */
  loadReturns() {
    return this.apiService.getAmendedReturn();
  }

  /** ADD_UPDATE: AMENDED_RETURN API PAYLOAD */
  addUpdateAmendedReturnPayload(field: AmendmentDTO) {
    const payload: AmendmentDTO = {
      amendedID: field.amendedID,
      fileDate: field.fileDate,
      issueYear: field.issueYear,
      status: field.status,
      countOfID: Number(field.countOfID),
      dateCreation: this.currentDate,
      dateModification: this.currentDate,
    };
    return payload;
  }

  /** ADD: AMENDED_RETURN API (Create) */
  addAmendedReturn(payload: AmendmentDTO): void {
    this.apiService.addAmendedReturn(payload).subscribe({
      next: () => {
        this.toastService.success('Amended Return created successfully.');
        this.triggerRefresh('Amended-Return');
        this.issuesService.clearFiltersCache();
      },
      error: (error) => {
        if (error.status === 409) {
          this.toastService.error('Duplicate Record: Amended ID already exists.');
        } else {
          console.error('Add Amended Return API Error', error);
        }
      },
    });
  }

  /** UPDATE: AMENDED_RETURN API */
  addUpdateAmendedReturn(payload: AmendmentDTO): void {
    this.apiService.updateAmendedReturn(payload).subscribe({
      next: () => {
        this.toastService.success('Amended Return updated successfully.');
        this.triggerRefresh('Amended-Return');
        this.issuesService.clearFiltersCache();
      },
      error: (error) => {
        console.error('Update Amended Return API Error', error);
      },
    });
  }

  /** DELETE: AMENDED_RETURN API */
  deleteAmendedReturn(id: string): void {
    this.apiService
      .deleteAmendedReturn(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.success('Amended Return deleted successfully.');
          this.triggerRefresh('Amended-Return');
          this.issuesService.clearFiltersCache();
        },
        error: (error) => {
          console.error('Delete Amended-Return API Error', error);
        },
      });
  }

  /** =========
   * ALL_ISSUES_TAB: GET, UPDATE, DELETE
   */

  /** GET */
  loadAllIssues() {
    return this.apiService.getAllIssuesList();
  }

  /** UPDATE */
  updateAllIssue(payload: AllIssueDTO, isEditCase?: boolean): void {
    this.issuesService.saveOrUpdateIssue(payload).subscribe({
      next: () => {
        this.toastService.success(`Issue: "${payload.issueID}" updated successfully.`);
        this.triggerRefresh('All-Issue');
        if (isEditCase) {
          this.triggerRefresh('ADJ_NAVIGATION');
        }
      },
      error: (error) => {
        console.error('All-Issue update error', error);
      },
    });
  }

  /** DELETE */
  deleteAllIssue(id: string): void {
    this.apiService
      .deleteIssueInAll(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.success(`Issue: ${id} deleted successfully.`);
          this.triggerRefresh('All-Issue');
        },
        error: (error) => {
          console.error('Delete All-Issue API Error', error);
        },
      });
  }

  getIssueByIssueId(issueID: string) {
    return this.apiService.getIssueById(issueID);
  }
}
